import client from '../../../config/api';
import sortTaskWithFolders from '../../../helpers/SortingTaksWithFolders';
import sortQuestionsHierarchy from '../../../helpers/SortQuestionsHierarchy';
import {
  LOCAL_TOGGLE_INTERNAL_REPORT,
  LOCAL_SET_REPORT_TYPE,
} from '../mutations/mutationTypes';

const VISUAL_DATA_CONDITIONS = ['Rubbed', 'Damaged', 'Pitted', 'Eroded'];

const isValidTimestamp = (timestamp) => {
  // Check if the input is a number and is finite
  if (!isNaN(timestamp) && isFinite(timestamp)) {
    // Convert the input to an integer
    const date = new Date(parseInt(timestamp, 10));

    // Check if the resulting date is valid
    return date.getTime() === parseInt(timestamp, 10);
  }
  return false;
};

const parseDate = (date) => {
  if (date instanceof Date) {
    const parsedDate = new Date(date);
    return parsedDate.toLocaleDateString();
  }

  if (date) {
    if (isValidTimestamp(date)) {
      return new Date(parseInt(date, 10)).toLocaleDateString();
    }

    return new Date(date).toLocaleDateString();
  }
  return '-';
};

const checkLastModifiedAtBeforeMigration = (inspection, task) => {
  if (inspection.lastModifiedAt && inspection.lastModifiedAt !== '-')
    return parseDate(inspection.lastModifiedAt);

  if (task.childConfig[0]) {
    task.childConfig[0].childConfig[0].childConfig.forEach((item) => {
      if (!item || !item.inputValues) return null;
      return item.inputValues.reduce((latest, input) => {
        if (input && input.bubbleModifierDate) {
          const date = new Date(input.bubbleModifierDate);
          return !latest || date > latest ? date : latest;
        }
        return latest;
      }, null);
    });
  }

  return '-';
};

const parseLengthAndDiameter = (
  inspection,
  maxNumberOfMeasurments,
  task,
  rowNumber
) => {
  const values = [];
  let hasMeasurements = false;
  for (let i = 0; i < maxNumberOfMeasurments; i++) {
    const m = inspection.measurements[i];
    if (
      !m ||
      isNaN(m.value) ||
      m.value === null ||
      m.value === undefined ||
      m.value === ''
    ) {
      values.push({
        value: '-',
        valueConverted: '-',
        name: `Value${i + 1}`,
      });
    } else {
      hasMeasurements = true;

      const isSameUnit = inspection.unit === m.unit;
      const inputUnit = `${m.unit}`;
      const report = {
        value: m.value,
        valueConverted: m.convertedInputValue,
        expected: inspection.expectedValue,
        expectedConverted: inspection.convertedExpectedValue,
        posTol: inspection.positiveTolerance,
        negTol: inspection.negativeTolerance,
        posTolConverted: inspection.convertedPositiveTolerance,
        negTolConverted: inspection.convertedNegativeTolerance,
      };

      let decimalPlaces;
      let decimalPlaceConverted;

      if (inspection.unit === 'mm') {
        decimalPlaces = isSameUnit ? 3 : 4;
        decimalPlaceConverted = isSameUnit ? 4 : 3;
      } else {
        decimalPlaces = isSameUnit ? 4 : 3;
        decimalPlaceConverted = isSameUnit ? 3 : 4;
      }
      if (inspection.unit === 'in' && m.unit === 'mm') {
        report.value = m.value / 25.4;
        report.expected = inspection.expectedValue;
        report.posTol = inspection.positiveTolerance;
        report.negTol = inspection.negativeTolerance;

        report.valueConverted = m.value;
        report.expectedConverted = inspection.expectedValue * 25.4;
        report.posTolConverted = inspection.positiveTolerance * 25.4;
        report.negTolConverted = inspection.negativeTolerance * 25.4;
      } else if (inspection.unit === 'in' && m.unit === 'in') {
        report.value = m.value;
        report.expected = inspection.expectedValue;
        report.posTol = inspection.positiveTolerance;
        report.negTol = inspection.negativeTolerance;

        report.valueConverted = m.value * 25.4;
        report.expectedConverted = inspection.expectedValue * 25.4;
        report.posTolConverted = inspection.positiveTolerance * 25.4;
        report.negTolConverted = inspection.negativeTolerance * 25.4;
      } else if (inspection.unit === 'mm' && m.unit === 'in') {
        report.value = m.value * 25.4;
        report.expected = inspection.expectedValue;
        report.posTol = inspection.positiveTolerance;
        report.negTol = inspection.negativeTolerance;

        report.valueConverted = m.value;
        report.expectedConverted = inspection.expectedValue / 25.4;
        report.posTolConverted = inspection.positiveTolerance / 25.4;
        report.negTolConverted = inspection.negativeTolerance / 25.4;
      } else if (inspection.unit === 'mm' && m.unit === 'mm') {
        report.value = m.value;
        report.expected = inspection.expectedValue;
        report.posTol = inspection.positiveTolerance;
        report.negTol = inspection.negativeTolerance;

        report.valueConverted = m.value / 25.4;
        report.expectedConverted = inspection.expectedValue / 25.4;
        report.posTolConverted = inspection.positiveTolerance / 25.4;
        report.negTolConverted = inspection.negativeTolerance / 25.4;
      }
      const measurement = parseFloat(report.value);
      const convertedMeasurement = parseFloat(report.valueConverted);

      let diff;
      let diffConverted;
      if (isSameUnit) {
        diff = measurement - parseFloat(report.expected);
        diffConverted =
          convertedMeasurement - parseFloat(report.expectedConverted);
      } else {
        diff = convertedMeasurement - parseFloat(report.expectedConverted);
        diffConverted = measurement - parseFloat(report.expected);
      }
      if (diff > 0) {
        if (isSameUnit) {
          diff = Math.max(0, diff - parseFloat(report.posTol));
        } else {
          diff = Math.max(0, diff - parseFloat(report.posTolConverted));
        }
      } else if (diff < 0) {
        if (isSameUnit) {
          diff = Math.min(0, diff + Math.abs(parseFloat(report.negTol)));
        } else {
          diff = Math.min(
            0,
            diff + Math.abs(parseFloat(report.negTolConverted))
          );
        }
      }

      if (diffConverted > 0) {
        if (isSameUnit) {
          diffConverted = Math.max(
            0,
            diffConverted - parseFloat(report.posTolConverted)
          );
        } else {
          diffConverted = Math.max(
            0,
            diffConverted - parseFloat(report.posTol)
          );
        }
      } else if (diffConverted < 0) {
        if (isSameUnit) {
          diffConverted = Math.min(
            0,
            diffConverted + Math.abs(parseFloat(report.negTolConverted))
          );
        } else {
          diffConverted = Math.min(
            0,
            diffConverted + Math.abs(parseFloat(report.negTol))
          );
        }
      }
      const value = `${m.value}`;
      const valueConverted = `${m.convertedInputValue}`;
      const hasValidDiff = !(
        Math.abs(diff) < 1e-10 ||
        diff === 0 ||
        isNaN(diff)
      );
      const hasValidDiffConverted = !(
        Math.abs(diffConverted) < 1e-10 ||
        diffConverted === 0 ||
        isNaN(diffConverted)
      );

      values.push({
        value,
        valueConverted,
        inputUnit,
        name: `Value${i + 1}`,

        ...(hasValidDiff && {
          diff: `(${diff.toFixed(decimalPlaces)})`,
        }),

        ...(hasValidDiffConverted && {
          diffConverted: `(${diffConverted.toFixed(decimalPlaceConverted)})`,
        }),
      });
    }
  }
  const lastModifiedAt = checkLastModifiedAtBeforeMigration(inspection, task);
  let partName;
  if (inspection.inTable) {
    partName = inspection.measurements[0].tableName
      ? inspection.measurements[0].tableName
      : rowNumber;
  }
  const lengthData = {
    label: inspection.number,
    values,
    expectedValue: inspection.expectedValue,
    tolerance1: `+${inspection.positiveTolerance}`,
    tolerance2: `-${inspection.negativeTolerance}`,
    convertedUnit: inspection.convertedUnit,
    convertedExpectedValue: inspection.convertedExpectedValue,
    convertedTolerance1: `+${inspection.convertedPositiveTolerance}`,
    convertedTolerance2: `-${inspection.convertedNegativeTolerance}`,
    description: inspection.description,
    hasDescription: inspection.hasDescription,
    unit: inspection.unit,
    lastModifiedBy:
      inspection.lastModifiedBy && hasMeasurements
        ? inspection.lastModifiedBy
        : '-',
    lastModifiedAt: hasMeasurements ? lastModifiedAt : '-',
    inTable: inspection.inTable,
    partName: partName || '-',
  };
  return lengthData;
};

const parseRunOutData = (
  inspection,
  maxNumberOfMeasurments,
  task,
  rowNumber
) => {
  const values = [];
  let decimalPlaces;
  let decimalPlaceConverted;
  const incrementValue = Math.floor(360 / maxNumberOfMeasurments);
  let hasMeasurements = false;
  let tir;
  let tirDiff;
  let tirConverted;
  let tirDiffConverted;

  if (inspection.multipleMeasurements) {
    let minMeasurement = +Infinity;
    let maxMeasurement = -Infinity;
    let minMeasurementConverted = +Infinity;
    let maxMeasurementConverted = -Infinity;
    let unitInput = '';
    for (let i = 0; i < maxNumberOfMeasurments; i++) {
      const m = inspection.measurements[i];
      unitInput = m.unit;
      if (!m) {
        values.push({
          value: '-',
          valueConverted: '-',
          name: `${incrementValue * i} Degrees`,
        });
      } else {
        const measurementValue = m.value;
        const measurementValueConverted = m.convertedInputValue;
        const inputUnit = `${m.unit}`;
        if (
          isNaN(measurementValue) ||
          measurementValue === null ||
          measurementValue === undefined ||
          measurementValue === ''
        ) {
          values.push({
            value: '-',
            valueConverted: '-',
            inputUnit,
            name: `${incrementValue * i} Degrees`,
          });
        } else {
          hasMeasurements = true;
          let measurementValueAsNumber = '';
          let measurementValueAsNumberConverted = '';
          if (inspection.unit === 'in' && inputUnit === 'in') {
            measurementValueAsNumber = parseFloat(measurementValue);
            measurementValueAsNumberConverted = parseFloat(
              measurementValueConverted
            );
          } else if (inspection.unit === 'in' && inputUnit === 'mm') {
            measurementValueAsNumber = parseFloat(measurementValueConverted);
            measurementValueAsNumberConverted = parseFloat(measurementValue);
          }

          if (inspection.unit === 'mm' && inputUnit === 'mm') {
            measurementValueAsNumber = parseFloat(measurementValue);
            measurementValueAsNumberConverted = parseFloat(
              measurementValueConverted
            );
          } else if (inspection.unit === 'mm' && inputUnit === 'in') {
            measurementValueAsNumber = parseFloat(measurementValueConverted);
            measurementValueAsNumberConverted = parseFloat(measurementValue);
          }
          values.push({
            value: `${measurementValue}`,
            valueConverted: `${measurementValueAsNumberConverted}`,
            inputUnit,
            name: `${incrementValue * i} Degrees`,
          });

          minMeasurement = Math.min(minMeasurement, measurementValueAsNumber);
          maxMeasurement = Math.max(maxMeasurement, measurementValueAsNumber);

          minMeasurementConverted = Math.min(
            minMeasurementConverted,
            measurementValueAsNumberConverted
          );
          maxMeasurementConverted = Math.max(
            maxMeasurementConverted,
            measurementValueAsNumberConverted
          );
        }
      }
    }
    if (minMeasurement === +Infinity || maxMeasurement === -Infinity) {
      tir = '-';
    } else {
      const isSameUnit = inspection.unit === unitInput;
      if (inspection.unit === 'mm') {
        decimalPlaces = isSameUnit ? 3 : 4;
        decimalPlaceConverted = isSameUnit ? 4 : 3;
      } else {
        decimalPlaces = isSameUnit ? 4 : 3;
        decimalPlaceConverted = isSameUnit ? 3 : 4;
      }
      const tirAsNumber = maxMeasurement - minMeasurement;
      const tirAsNumberConverted =
        maxMeasurementConverted - minMeasurementConverted;
      tir = tirAsNumber.toFixed(decimalPlaces);
      tirConverted = tirAsNumberConverted.toFixed(decimalPlaceConverted);
      const tirDiffAsNumber = Math.abs(
        parseFloat(inspection.tolerance) - tirAsNumber
      );
      const tirDiffAsNumberConverted = Math.abs(
        parseFloat(inspection.convertedTolerance) - tirAsNumberConverted
      );
      if (tirDiffAsNumber !== 0) {
        tirDiff = `(${tirDiffAsNumber.toFixed(decimalPlaces)})`;
      }
      if (tirDiffAsNumberConverted !== 0) {
        tirDiffConverted = `(${tirDiffAsNumberConverted.toFixed(
          decimalPlaceConverted
        )})`;
      }
    }
  } else {
    const tirValue = inspection.measurements[0].value;
    const tirValueConverted = inspection.measurements[0].convertedInputValue;
    const inputUnit = inspection.measurements[0].unit;
    const isSameUnit = inspection.unit === inputUnit;
    if (inspection.unit === 'mm') {
      decimalPlaces = isSameUnit ? 3 : 4;
      decimalPlaceConverted = isSameUnit ? 4 : 3;
    } else {
      decimalPlaces = isSameUnit ? 4 : 3;
      decimalPlaceConverted = isSameUnit ? 3 : 4;
    }
    if (
      isNaN(tirValue) ||
      tirValue === null ||
      tirValue === undefined ||
      tirValue === ''
    ) {
      values.push({
        value: '-',
        valueConverted: '-',
        name: 'TIR',
      });
      values.push({
        value: '-°',
        valueConverted: '-°',
        name: 'Position',
      });
    } else {
      hasMeasurements = true;
      let diff;
      let diffConverted;
      if (isSameUnit) {
        diff = parseFloat(tirValue) - parseFloat(inspection.tolerance);
        diffConverted =
          parseFloat(tirValueConverted) -
          parseFloat(inspection.convertedTolerance);
      } else {
        diff = parseFloat(tirValue) - parseFloat(inspection.convertedTolerance);
        diffConverted =
          parseFloat(tirValueConverted) - parseFloat(inspection.tolerance);
      }

      const hasValidDiff = !(
        Math.abs(diff) < 1e-10 ||
        diff <= 0 ||
        isNaN(diff)
      );
      const hasValidDiffConverted = !(
        Math.abs(diffConverted) < 1e-10 ||
        diffConverted <= 0 ||
        isNaN(diffConverted)
      );

      values.push({
        value: `${tirValue}`,
        valueConverted: `${tirValueConverted}`,
        ...(hasValidDiff && {
          diff: `(${diff.toFixed(decimalPlaces)})`,
        }),

        ...(hasValidDiffConverted && {
          diffConverted: `(${diffConverted.toFixed(decimalPlaceConverted)})`,
        }),
        inputUnit,
        name: 'TIR',
      });

      if (!inspection.measurements[1]) {
        values.push({
          value: '-°',
          name: 'Position',
        });
      } else {
        values.push({
          value:
            inspection?.measurements?.[1]?.value !== '' &&
            inspection?.measurements?.[1]?.value !== null &&
            Number.isFinite(Number(inspection?.measurements?.[1]?.value))
              ? `${parseFloat(inspection.measurements[1].value).toFixed(0)}°`
              : '-°',
          name: 'Position',
        });
      }
    }
  }
  const lastModifiedAt = checkLastModifiedAtBeforeMigration(inspection, task);

  let partName;
  if (inspection.inTable) {
    partName = inspection.measurements[0].tableName
      ? inspection.measurements[0].tableName
      : rowNumber;
  }

  const runOutData = {
    label: inspection.number,
    values,
    expectedValue: inspection.tolerance,
    expectedValueConverted: inspection.convertedTolerance,
    unit: inspection.unit,
    description: inspection.description,
    hasDescription: inspection.hasDescription,
    lastModifiedBy:
      inspection.lastModifiedBy && hasMeasurements !== '-'
        ? inspection.lastModifiedBy
        : '-',
    lastModifiedAt: hasMeasurements ? lastModifiedAt : '-',
    hasMultipleMeasurements: inspection.multipleMeasurements,
    tir,
    tirDiff,
    tirConverted,
    tirDiffConverted,
    inTable: inspection.inTable,
    partName: partName || '-',
  };

  return runOutData;
};

const parseVisualData = (inspection, task, rowNumber) => {
  const visualData = [];
  let isOk = true;

  inspection.measurements.forEach((m, i) => {
    if (!m) return;
    const lastModifiedAt = checkLastModifiedAtBeforeMigration(inspection, task);
    const measurementValue = m.value;
    let partName;
    if (inspection.inTable) {
      partName = inspection.measurements[0].tableName
        ? inspection.measurements[0].tableName
        : rowNumber;
    }
    if (measurementValue && measurementValue !== 'notCompleted') {
      isOk = false;
      visualData.push({
        label: inspection.number,
        condition: VISUAL_DATA_CONDITIONS[i],
        value: measurementValue,
        description: inspection.description,
        hasDescription: inspection.hasDescription,
        lastModifiedBy:
          inspection.lastModifiedBy && lastModifiedAt
            ? inspection.lastModifiedBy
            : '-',
        lastModifiedAt,
        partName: partName || '-',
        inTable: inspection.inTable,
      });
    }
  });

  const notCompleted = inspection.measurements.every((measurement) => {
    if (measurement) return measurement.value === 'notCompleted';
    return false;
  });

  let partName;
  if (inspection.inTable) {
    partName = inspection.measurements[0].tableName
      ? inspection.measurements[0].tableName
      : rowNumber;
  }

  if (notCompleted) {
    visualData.push({
      label: inspection.number,
      condition: '-',
      value: '-',
      lastModifiedBy: '-',
      description: inspection.description,
      hasDescription: inspection.hasDescription,
      lastModifiedAt: '-',
      partName: partName || '-',
      inTable: inspection.inTable,
    });

    return visualData;
  }

  if (isOk) {
    visualData.push({
      label: inspection.number,
      condition: 'FINE',
      value: 'FINE',
      description: inspection.description,
      hasDescription: inspection.hasDescription,
      lastModifiedBy: inspection.lastModifiedBy
        ? inspection.lastModifiedBy
        : '-',
      lastModifiedAt: parseDate(inspection.lastModifiedAt),
      partName: partName || '-',
      inTable: inspection.inTable,
    });
  }

  return visualData;
};

const parseGenericData = (
  inspection,
  maxNumberOfMeasurments,
  task,
  rowNumber
) => {
  const genericData = [];
  const values = [];

  for (let i = 0; i < maxNumberOfMeasurments; i++) {
    const m = inspection.measurements[i];
    if (!m || m.value === '') {
      values.push({
        value: '-',
        valueConverted: '-',
        name: `Value${i + 1}`,
      });
    } else if (isNaN(m.value)) {
      values.push({
        value: m.value,
        valueConverted: m.convertedInputValue,
        inputUnit: m.unit,
        name: `Value${i + 1}`,
      });
    } else {
      const isSameUnit = inspection.unit === m.unit;
      const inputUnit = `${m.unit}`;
      const report = {
        value: m.value,
        valueConverted: m.convertedInputValue,
        expected: inspection.expectedValue,
        expectedConverted: inspection.convertedExpectedValue,
        posTol: inspection.positiveTolerance,
        negTol: inspection.negativeTolerance,
        posTolConverted: inspection.convertedPositiveTolerance,
        negTolConverted: inspection.convertedNegativeTolerance,
      };

      let decimalPlaces;
      let decimalPlaceConverted;

      if (inspection.unit === 'mm') {
        decimalPlaces = isSameUnit ? 3 : 4;
        decimalPlaceConverted = isSameUnit ? 4 : 3;
      } else {
        decimalPlaces = isSameUnit ? 4 : 3;
        decimalPlaceConverted = isSameUnit ? 3 : 4;
      }

      if (inspection.unit === 'in' && m.unit === 'mm') {
        report.value = m.value / 25.4;
        report.expected = inspection.expectedValue;
        report.posTol = inspection.positiveTolerance;
        report.negTol = inspection.negativeTolerance;

        report.valueConverted = m.value;
        report.expectedConverted = inspection.expectedValue * 25.4;
        report.posTolConverted = inspection.positiveTolerance * 25.4;
        report.negTolConverted = inspection.negativeTolerance * 25.4;
      } else if (inspection.unit === 'in' && m.unit === 'in') {
        report.value = m.value;
        report.expected = inspection.expectedValue;
        report.posTol = inspection.positiveTolerance;
        report.negTol = inspection.negativeTolerance;

        report.valueConverted = m.value * 25.4;
        report.expectedConverted = inspection.expectedValue * 25.4;
        report.posTolConverted = inspection.positiveTolerance * 25.4;
        report.negTolConverted = inspection.negativeTolerance * 25.4;
      } else if (inspection.unit === 'mm' && m.unit === 'in') {
        report.value = m.value * 25.4;
        report.expected = inspection.expectedValue;
        report.posTol = inspection.positiveTolerance;
        report.negTol = inspection.negativeTolerance;

        report.valueConverted = m.value;
        report.expectedConverted = inspection.expectedValue / 25.4;
        report.posTolConverted = inspection.positiveTolerance / 25.4;
        report.negTolConverted = inspection.negativeTolerance / 25.4;
      } else if (inspection.unit === 'mm' && m.unit === 'mm') {
        report.value = m.value;
        report.expected = inspection.expectedValue;
        report.posTol = inspection.positiveTolerance;
        report.negTol = inspection.negativeTolerance;

        report.valueConverted = m.value / 25.4;
        report.expectedConverted = inspection.expectedValue / 25.4;
        report.posTolConverted = inspection.positiveTolerance / 25.4;
        report.negTolConverted = inspection.negativeTolerance / 25.4;
      }
      const measurement = parseFloat(report.value);
      const convertedMeasurement = parseFloat(report.valueConverted);

      let diff;
      let diffConverted;
      if (isSameUnit) {
        diff = measurement - parseFloat(report.expected);
        diffConverted =
          convertedMeasurement - parseFloat(report.expectedConverted);
      } else {
        diff = convertedMeasurement - parseFloat(report.expectedConverted);
        diffConverted = measurement - parseFloat(report.expected);
      }
      if (diff > 0) {
        if (isSameUnit) {
          diff = Math.max(0, diff - parseFloat(report.posTol));
        } else {
          diff = Math.max(0, diff - parseFloat(report.posTolConverted));
        }
      } else if (diff < 0) {
        if (isSameUnit) {
          diff = Math.min(0, diff + Math.abs(parseFloat(report.negTol)));
        } else {
          diff = Math.min(
            0,
            diff + Math.abs(parseFloat(report.negTolConverted))
          );
        }
      }

      if (diffConverted > 0) {
        if (isSameUnit) {
          diffConverted = Math.max(
            0,
            diffConverted - parseFloat(report.posTolConverted)
          );
        } else {
          diffConverted = Math.max(
            0,
            diffConverted - parseFloat(report.posTol)
          );
        }
      } else if (diffConverted < 0) {
        if (isSameUnit) {
          diffConverted = Math.min(
            0,
            diffConverted + Math.abs(parseFloat(report.negTolConverted))
          );
        } else {
          diffConverted = Math.min(
            0,
            diffConverted + Math.abs(parseFloat(report.negTol))
          );
        }
      }

      const value = `${m.value}`;
      const valueConverted = `${m.convertedInputValue}`;
      if (parseFloat(diff.toFixed(decimalPlaces)) !== 0.0 && !isNaN(diff)) {
        values.push({
          value,
          valueConverted,
          inputUnit,
          diff: `(${diff.toFixed(decimalPlaces)})`,
          diffConverted: `(${diffConverted.toFixed(decimalPlaceConverted)})`,
          name: `Value${i + 1}`,
        });
      } else {
        values.push({
          value,
          valueConverted,
          inputUnit,
          name: `Value${i + 1}`,
        });
      }
    }
  }
  let partName;
  if (inspection.inTable) {
    partName = inspection.measurements[0].tableName
      ? inspection.measurements[0].tableName
      : rowNumber;
  }
  const lastModifiedAt = checkLastModifiedAtBeforeMigration(inspection, task);
  const expectedValue =
    inspection.expectedValue && !isNaN(inspection.expectedValue)
      ? inspection.expectedValue
      : undefined;

  const expectedValueConverted =
    inspection.convertedExpectedValue &&
    !isNaN(inspection.convertedExpectedValue)
      ? inspection.convertedExpectedValue
      : undefined;

  genericData.push({
    label: inspection.number,
    measurementName: inspection.measurementName
      ? inspection.measurementName
      : '-',
    values: values || '-',
    description: inspection.description,
    hasDescription: inspection.hasDescription,
    lastModifiedBy:
      inspection.lastModifiedBy && lastModifiedAt !== '-'
        ? inspection.lastModifiedBy
        : '-',
    lastModifiedAt,
    partName: partName || '-',
    inTable: inspection.inTable,
    expectedValue,
    tolerance1: expectedValue ? inspection.positiveTolerance : '-',
    tolerance2: expectedValue ? inspection.negativeTolerance : '-',
    unit: expectedValue ? inspection.unit : undefined,

    convertedUnit: expectedValueConverted
      ? inspection.convertedUnit
      : undefined,
    convertedExpectedValue: expectedValueConverted,
    convertedTolerance1: expectedValueConverted
      ? inspection.convertedPositiveTolerance
      : '-',
    convertedTolerance2: expectedValueConverted
      ? inspection.convertedNegativeTolerance
      : '-',
  });

  return genericData;
};

const checkImagesNamesForUUID = (images) => {
  const uuidPattern =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const toReturn = images.map((image) => {
    const isUUID = uuidPattern.test(image.name.split('.')[0]);
    return {
      ...image,
      name: isUUID ? '' : image.name,
    };
  });
  return toReturn;
};

const renameKeyPathToUrl = (array) => {
  array.forEach((obj) => {
    if (obj.path) {
      obj.src = obj.path;
      delete obj.path;
    }
  });
  return array;
};

const parseAttachmentInstructionsData = (questions) => {
  const data = [];
  questions.forEach((q) => {
    if (!q) return;
    if (q.image && q.image.length > 0) {
      q.image = checkImagesNamesForUUID(q.image);
      data.push({
        questionId: q._id,
        question: q.question,
        images: renameKeyPathToUrl(q.image),
      });
    }
  });
  return data;
};

const checkQuestionLastModifiedAtBeforeMigration = (question, task) => {
  if (question.lastModifiedAt && question.lastModifiedAt !== '-')
    return parseDate(question.lastModifiedAt);

  if (task.childConfig[0]) {
    let lastModifiedAt = '-';
    const item = task.childConfig[0].childConfig.find(
      (i) =>
        i.lastModifiedBy !== undefined &&
        (i.lastModifiedBy.modified !== undefined ||
          i.lastModifiedBy.date !== undefined)
    );

    if (!item) return '-';

    if (item.lastModifiedBy.date) {
      return item.lastModifiedBy.date;
    }

    if (item.lastModifiedBy.modified) {
      lastModifiedAt = Date.parse(item.lastModifiedBy.modified);
      lastModifiedAt = new Date(
        parseInt(lastModifiedAt, 10)
      ).toLocaleDateString();
    }
    return lastModifiedAt;
  }

  return '-';
};

const parseSmartTableData = (tableData) => {
  const tableDataParsed = [];
  tableData.forEach((row, rowIndex) => {
    tableDataParsed[rowIndex] = [];
    row.forEach((cell, cellIndex) => {
      if (typeof cell === 'string') {
        tableDataParsed[rowIndex][cellIndex] = cell;
      }

      if (
        typeof cell === 'object' &&
        cell !== null &&
        cellIndex > 0 &&
        rowIndex
      ) {
        switch (cell.type) {
          case 'action':
            tableDataParsed[rowIndex][cellIndex] = {
              value: cell.value ? 'Done' : '-',
            };
            tableDataParsed[rowIndex][cellIndex].lastUpdatedBy =
              cell.lastUpdatedBy;
            tableDataParsed[rowIndex][cellIndex].lastModifiedAt =
              cell.lastModifiedAt;
            break;

          case 'textnumber':
            tableDataParsed[rowIndex][cellIndex] = { value: cell.value };
            tableDataParsed[rowIndex][cellIndex].lastUpdatedBy =
              cell.lastUpdatedBy;
            tableDataParsed[rowIndex][cellIndex].lastModifiedAt =
              cell.lastModifiedAt;
            break;

          case 'yesno': {
            let answer = '-';
            if (
              !cell.value ||
              cell.value === null ||
              cell.value === undefined
            ) {
              answer = '-';
            } else {
              answer = cell.value === '' ? '-' : cell.value.toUpperCase();
            }
            tableDataParsed[rowIndex][cellIndex] = { value: answer };
            tableDataParsed[rowIndex][cellIndex].lastUpdatedBy =
              cell.lastUpdatedBy;
            tableDataParsed[rowIndex][cellIndex].lastModifiedAt =
              cell.lastModifiedAt;

            break;
          }

          case 'multiplechoice': {
            let answer = [];
            cell.options.map((mc) => {
              if (mc.checked) {
                const answerText =
                  mc?.defaultMC || mc.text?.defaultMC || mc?.text;
                answer.push(answerText);
              }
            });
            if (answer.length === 0) {
              answer = '-';
            } else {
              answer = answer.join(', ');
            }

            tableDataParsed[rowIndex][cellIndex] = { value: answer };
            tableDataParsed[rowIndex][cellIndex].lastUpdatedBy =
              cell.lastUpdatedBy;
            tableDataParsed[rowIndex][cellIndex].lastModifiedAt =
              cell.lastModifiedAt;
            break;
          }

          case 'singlechoice': {
            let answer = [];
            cell.options.map((mc) => {
              if (mc.checked) {
                const answerText =
                  mc?.defaultMC || mc.text?.defaultMC || mc?.text;
                answer.push(answerText);
              }
            });
            if (answer.length === 0) {
              answer = '-';
            } else {
              answer = answer.join(', ');
            }

            tableDataParsed[rowIndex][cellIndex] = { value: answer };
            tableDataParsed[rowIndex][cellIndex].lastUpdatedBy =
              cell.lastUpdatedBy;
            tableDataParsed[rowIndex][cellIndex].lastModifiedAt =
              cell.lastModifiedAt;
            break;
          }

          default:
            throw new Error(`Failed to parse cell type '${cell.type}'`);
        }
      }
    });
  });

  return tableDataParsed;
};

const parseQuestionsData = (questions, subtabelsQuestions, task) => {
  const data = [];
  questions.sort((a, b) => parseInt(a.label, 10) - parseInt(b.label, 10));
  questions.forEach((q) => {
    if (!q) return;

    let answer = '-';
    switch (q.type) {
      case 'yesno':
        if (!q.yesOrNo || q.yesOrNo === null || q.yesOrNo === undefined) {
          answer = '-';
        } else {
          answer = q.yesOrNo === '' ? '-' : q.yesOrNo.toUpperCase();
        }

        break;

      case 'textnumber':
        answer = q.inputValue !== '' ? q.inputValue : '-';
        break;

      case 'action':
        answer = q.checked ? 'Done' : '-';
        break;

      case 'multiplechoice':
        answer = [];
        q.multipleChoices.map((mc) => {
          if (mc.checked) {
            const answerText = mc?.defaultMC || mc.text?.defaultMC || mc?.text;
            answer.push(answerText);
          }
        });
        if (answer.length === 0) {
          answer = '-';
        } else {
          answer = answer.join(', ');
        }
        break;

      case 'singlechoice':
        answer = [];
        q.multipleChoices.map((mc) => {
          if (mc.checked) {
            const answerText = mc?.defaultMC || mc.text?.defaultMC || mc?.text;
            answer.push(answerText);
          }
        });
        if (answer.length === 0) {
          answer = '-';
        } else {
          answer = answer.join(', ');
        }
        break;

      case 'tablequestion':
        answer = 'See below';
        subtabelsQuestions.push({
          tableData: q.tableData,
          tableImg: q.tableImg,
          tableColumn: q.tableColumn,
          tableRow: q.tableRow,
          question: q.question,
        });
        break;

      case 'smarttablequestion': {
        const tableData = parseSmartTableData(q.tableData);
        answer = 'See below';
        subtabelsQuestions.push({
          tableData,
          tableImg: q.tableImg,
          tableColumn: q.tableColumn,
          tableRow: q.tableRow,
          question: q.question,
        });
        break;
      }

      case 'importcsv':
        subtabelsQuestions.push({
          tableData: q.tableData,
          tableImg: q.tableImg,
          tableColumn: q.tableColumn,
          tableRow: q.tableRow,
          question: q.question,
        });
        break;
      default:
        throw new Error(`Failed to parse question type '${q.type}'`);
    }
    let lastModifiedAt = '-';
    try {
      lastModifiedAt = checkQuestionLastModifiedAtBeforeMigration(q, task);
    } catch (error) {
      console.error(error);
    }

    data.push({
      question: q.question,
      answer,
      attachment: q.image.length > 0 ? q.image.length : '-',
      lastModifiedBy:
        q.lastModifiedBy && lastModifiedAt !== '-' ? q.lastModifiedBy : '-',
      lastModifiedAt,
    });
  });
  return data;
};

const adaptDiagramForReport = (task, bubbles) => {
  if (task.taskType === 'diagram') {
    if (!task.diagram) {
      task.diagram = {};
    }

    task.diagram.annotations = [];
    task.diagram.inspections = [];

    task.lengthData = [];
    task.diameterData = [];
    task.runoutData = [];
    task.visualData = [];
    task.genericData = [];
    task.attachmentData = [];

    if (bubbles) {
      let maxLength = 0;
      let maxDiameter = 0;
      let maxRunout = 0;
      let maxGeneric = 0;
      bubbles.sort((a, b) => a.number - b.number);
      bubbles.forEach((bubble) => {
        switch (bubble.type) {
          case 'length':
            if (bubble.measurementCount > maxLength) {
              maxLength = bubble.measurementCount;
            }
            break;

          case 'diameter':
            if (bubble.measurementCount > maxDiameter) {
              maxDiameter = bubble.measurementCount;
            }
            break;

          case 'runout':
            if (bubble.measurementCount > maxRunout) {
              maxRunout = bubble.measurementCount;
            }
            break;

          case 'generic':
            if (bubble.measurementCount > maxGeneric) {
              maxGeneric = bubble.measurementCount;
            }
            break;
          default:
            break;
        }
      });
      bubbles.forEach((bubble) => {
        if (['text', 'arrow', 'line', 'link'].includes(bubble.type)) {
          task.diagram.annotations.push(bubble);
        } else {
          task.diagram.inspections.push(bubble);
          switch (bubble.type) {
            case 'length':
              if (bubble.inTable) {
                bubble.tableMeasurements.forEach((measurement, index) => {
                  const tableBubble = { ...bubble };
                  tableBubble.measurements = measurement;
                  task.lengthData.push(
                    parseLengthAndDiameter(
                      tableBubble,
                      maxLength,
                      task,
                      index + 1
                    )
                  );

                  let partName;
                  if (tableBubble.inTable) {
                    partName = tableBubble.measurements[0].tableName
                      ? tableBubble.measurements[0].tableName
                      : index + 1;
                  }
                  if (
                    measurement[0].images.length > 0 ||
                    measurement[0].comment
                  )
                    task.attachmentData.push({
                      bubbleId: bubble._id,
                      label: `${bubble.number} - ${partName || '-'}`,
                      images: measurement[0].images,
                      comment: measurement[0].comment,
                      description: bubble.description,
                    });
                });
              } else {
                task.lengthData.push(
                  parseLengthAndDiameter(bubble, maxLength, task)
                );
              }
              task.lengthData.sort((a, b) => a.label - b.label);
              break;

            case 'diameter':
              if (bubble.inTable) {
                bubble.tableMeasurements.forEach((measurement, index) => {
                  const tableBubble = { ...bubble };
                  tableBubble.measurements = measurement;
                  task.diameterData.push(
                    parseLengthAndDiameter(
                      tableBubble,
                      maxDiameter,
                      task,
                      index + 1
                    )
                  );

                  let partName;
                  if (tableBubble.inTable) {
                    partName = tableBubble.measurements[0].tableName
                      ? tableBubble.measurements[0].tableName
                      : index + 1;
                  }

                  if (
                    measurement[0].images.length > 0 ||
                    measurement[0].comment
                  )
                    task.attachmentData.push({
                      bubbleId: bubble._id,
                      label: `${bubble.number} - ${partName || '-'}`,
                      images: measurement[0].images,
                      comment: measurement[0].comment,
                      description: bubble.description,
                    });
                });
              } else {
                task.diameterData.push(
                  parseLengthAndDiameter(bubble, maxDiameter, task)
                );
              }
              task.diameterData.sort((a, b) => a.label - b.label);
              break;

            case 'runout':
              if (bubble.inTable) {
                bubble.tableMeasurements.forEach((measurement, index) => {
                  const tableBubble = { ...bubble };
                  tableBubble.measurements = measurement;
                  task.runoutData.push(
                    parseRunOutData(tableBubble, maxRunout, task, index + 1)
                  );
                  let partName;
                  if (tableBubble.inTable) {
                    partName = tableBubble.measurements[0].tableName
                      ? tableBubble.measurements[0].tableName
                      : index + 1;
                  }
                  if (
                    measurement[0].images.length > 0 ||
                    measurement[0].comment
                  )
                    task.attachmentData.push({
                      bubbleId: bubble._id,
                      label: `${bubble.number} - ${partName || '-'}`,
                      images: measurement[0].images,
                      comment: measurement[0].comment,
                      description: bubble.description,
                    });
                });
              } else {
                task.runoutData.push(parseRunOutData(bubble, maxRunout, task));
              }
              task.runoutData.sort((a, b) => a.label - b.label);
              break;

            case 'visual':
              if (bubble.inTable) {
                bubble.tableMeasurements.forEach((measurement, index) => {
                  const tableBubble = { ...bubble };
                  tableBubble.measurements = measurement;
                  task.visualData.push(
                    ...parseVisualData(tableBubble, task, index + 1)
                  );
                  let partName;
                  if (tableBubble.inTable) {
                    partName = tableBubble.measurements[0].tableName
                      ? tableBubble.measurements[0].tableName
                      : index + 1;
                  }

                  if (
                    measurement[0].images.length > 0 ||
                    measurement[0].comment
                  )
                    task.attachmentData.push({
                      bubbleId: bubble._id,
                      label: `${bubble.number} - ${partName || '-'}`,
                      images: measurement[0].images,
                      comment: measurement[0].comment,
                      description: bubble.description,
                    });
                });
              } else {
                task.visualData.push(...parseVisualData(bubble, task));
              }
              task.visualData.sort((a, b) => a.label - b.label);
              break;

            case 'generic':
              if (bubble.inTable) {
                bubble.tableMeasurements.forEach((measurement, index) => {
                  const tableBubble = { ...bubble };
                  tableBubble.measurements = measurement;
                  task.genericData.push(
                    ...parseGenericData(
                      tableBubble,
                      maxGeneric,
                      task,
                      index + 1
                    )
                  );

                  let partName;
                  if (tableBubble.inTable) {
                    partName = tableBubble.measurements[0].tableName
                      ? tableBubble.measurements[0].tableName
                      : index + 1;
                  }

                  if (
                    measurement[0].images.length > 0 ||
                    measurement[0].comment
                  )
                    task.attachmentData.push({
                      bubbleId: bubble._id,
                      label: `${bubble.number} - ${partName || '-'}`,
                      images: measurement[0].images,
                      comment: measurement[0].comment,
                      description: bubble.description,
                    });
                });
              } else {
                task.genericData.push(
                  ...parseGenericData(bubble, maxGeneric, task)
                );
              }
              task.genericData.sort((a, b) => a.label - b.label);
              break;

            case 'freefinding':
              break;

            case 'rat':
              break;

            case 'position-image':
              break;
            default:
              throw new Error(`Failed to parse bubble type ${bubble.type}`);
          }
          if (
            bubble.inspectorComment ||
            bubble.images.length > 0 ||
            (bubble.type === 'freefinding' && bubble.hasDescription)
          ) {
            task.attachmentData.push({
              bubbleId: bubble._id,
              label: bubble.number,
              images: bubble.images,
              comment: bubble.inspectorComment,
              description: bubble.description,
              isFreeFinding: bubble.type === 'freefinding',
            });
          }
        }
      });
    }
  }
};

const adaptInstructionForReport = (task, questions) => {
  task.attachmentData = [];
  if (questions) {
    questions.sort((a, b) => a.label - b.label);
    const subtabelsQuestions = [];
    task.instructionData = parseQuestionsData(
      questions,
      subtabelsQuestions,
      task
    );
    task.subtabelsQuestions = subtabelsQuestions;
  }

  task.attachmentData.push(...parseAttachmentInstructionsData(questions));
};

const processFolder = async (tasksToProcess) => {
  const tasks = [];
  const processSubFolders = [];
  tasksToProcess.forEach((task) => {
    tasks.push(task);
    if (task.taskType === 'folder') {
      processSubFolders.push(
        client
          .get(`/task/job/${task.jobId}?folderId=${task._id}`)
          .then(({ data: folderTasks }) => processFolder(folderTasks, client))
      );
    }
  });

  await Promise.all(processSubFolders).then((subfoldersTasks) => {
    subfoldersTasks.forEach((subfolderTasks) => {
      tasks.push(...subfolderTasks);
    });
  });

  return tasks;
};

export default {
  GET_REPORT_VIEW: async ({ commit }, jobId) => {
    try {
      const reportView = {};
      const { data: job } = await client.get(`/job/${jobId}`);
      reportView.job = job;

      const { data: rootTasks } = await client.get(`/task/job/${jobId}`);

      let tasks = await processFolder(rootTasks);
      tasks = sortTaskWithFolders(tasks);
      reportView.tasks = tasks;

      const additionalData = [];
      tasks.forEach((task) => {
        if (task.taskType === 'diagram') {
          additionalData.push(
            client
              .get(`/bubbles/${task._id}`)
              .then(({ data: bubbles }) => adaptDiagramForReport(task, bubbles))
          );
        } else if (task.taskType === 'instruction') {
          additionalData.push(
            client.get(`/questions/${task._id}`).then(({ data: questions }) => {
              const sortedQuestions = sortQuestionsHierarchy(questions);
              return adaptInstructionForReport(task, sortedQuestions);
            })
          );
        }
      });
      await Promise.all(additionalData);

      commit('SET_REPORT_VIEW', reportView);
      if (job.excludedItems) {
        commit('SET_EXCLUDED_ITEMS', job.excludedItems);
      } else {
        commit('SET_EXCLUDED_ITEMS', []);
      }
    } catch (error) {
      console.error(error);
    }
  },

  UPDATE_ATTACHMENT_IMAGE: async (
    _,
    { bubbleId, imageUrl, imageName, imgDescription }
  ) => {
    try {
      const { data } = await client.post(
        `report/update-attachment-image/${bubbleId}`,
        {
          imageUrl,
          imageName,
          imgDescription,
        }
      );
      return data;
    } catch (error) {
      console.error(error);
    }
  },

  TOGGLE_EXCLUDE_ITEMS: async ({ commit, rootState, state }) => {
    if (state.enableExcludeItems) {
      try {
        await client.put(`/job/exclude-items/${rootState.currentJob._id}`, {
          excludedItems: state.excludedItems,
        });
      } catch (error) {
        console.error(error);
      }
    }

    commit('TOGGLE_EXCLUDE_ITEMS');
  },

  TOGGLE_INTERNAL_REPORT: ({ commit }, value) => {
    commit(LOCAL_TOGGLE_INTERNAL_REPORT, value);
  },

  SET_REPORT_TYPE: ({ commit }, value) => {
    commit(LOCAL_SET_REPORT_TYPE, value);
  },

  ITEM_TO_EXCLUDE: ({ commit }, { id, value }) => {
    commit('ITEM_TO_EXCLUDE', { id, value });
  },

  DELETE_REVISION: async ({ commit }, { jobId, revision }) => {
    try {
      await client.delete(`/report/revision/${jobId}/${revision.id}`);
      commit('JobsModule/DELETE_REPORT_REVISION', revision, { root: true });
    } catch (error) {
      console.error(error);
    }
  },

  UPDATE_CUSTOMER_OUT_OF_TOLERANCE: async ({}, { id, value }) => {
    try {
      await client.put(`/task/toggle-customer-colors/${id}`, {
        customerOutOfTolerance: value,
      });
    } catch (error) {
      console.error(error);
    }
  },

  EXPORT_TASK: async (_, { taskId, isInternalReport, canvas }) => {
    try {
      const { data } = await client.post(`/report/task/${taskId}`, {
        isInternalReport,
        canvas,
      });
      return data;
    } catch (error) {
      console.error(error);
    }
  },
};
